import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	CreateOrganizationCustomFieldDto,
	UpdateOrganizationCustomFieldDto,
} from '@dealstack/shared/lib/generated';
import {
	createOrganizationCustomField,
	deleteOrganizationCustomField,
	getOrganizationCustomFields,
	updateOrganizationCustomField,
} from '@dealstack/frontend/shared/dataExchange/services/programme-defaults.service.ts';

const queryKey = (organizationId: string) => [
	API_PATHS.PROGRAMME_DEFAULTS_CUSTOM_FIELD_ORGANIZATIONID_GET,
	organizationId,
];

export const useOrganizationCustomFields = (organizationId: string) => {
	const queryClient = useQueryClient();
	const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKey(organizationId) });

	const { data: customFields, isLoading } = useQuery({
		queryKey: queryKey(organizationId),
		queryFn: () => getOrganizationCustomFields(organizationId),
		enabled: !!organizationId,
		select: (data) => data.payload ?? [],
	});

	const { mutate: create, isPending: isCreating } = useMutation({
		mutationFn: (body: CreateOrganizationCustomFieldDto) =>
			createOrganizationCustomField({ organizationId, body }),
		onSuccess: invalidate,
	});

	const { mutate: update, isPending: isUpdating } = useMutation({
		mutationFn: ({
			customFieldId,
			body,
		}: {
			customFieldId: string;
			body: UpdateOrganizationCustomFieldDto;
		}) => updateOrganizationCustomField({ organizationId, customFieldId, body }),
		onSuccess: invalidate,
	});

	const { mutate: remove, isPending: isRemoving } = useMutation({
		mutationFn: (customFieldId: string) =>
			deleteOrganizationCustomField({ organizationId, customFieldId }),
		onSuccess: invalidate,
	});

	return {
		customFields: customFields ?? [],
		isLoading,
		isSaving: isCreating || isUpdating || isRemoving,
		create,
		update,
		remove,
	};
};
